import { QueryClient, useQuery } from '@tanstack/react-query';

/**
 * Optimized cache configuration for different types of API responses
 */
const cacheConfig = {
  // User data - changes infrequently, cache longer
  user: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },
  // Machine data - changes moderately, medium cache
  machines: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },
  // Pricing data - relatively stable, cache longer
  pricing: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  },
  // P2P data - changes frequently, shorter cache
  p2p: {
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  },
  // Chat/Store data - user-specific, medium cache
  store: {
    staleTime: 3 * 60 * 1000, // 3 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  },
  // Cost calculations - can be cached briefly
  costs: {
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
  },
  // Default for other data
  default: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  },
};

// Enhanced API client with optimized caching and performance
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default cache settings
      staleTime: cacheConfig.default.staleTime,
      gcTime: cacheConfig.default.gcTime,
      // Optimized retry logic
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors (client errors)
        if (typeof error === 'object' && error !== null && 'status' in error) {
          const apiError = error as { status: number };
          if (apiError.status >= 400 && apiError.status < 500) {
            return false;
          }
        }
        // Don't retry on network errors more than once
        if (typeof error === 'object' && error !== null && 'isNetworkError' in error) {
          return failureCount < 1;
        }
        // Retry server errors up to 2 times
        return failureCount < 2;
      },
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Enable background refetching for better UX
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Disable refetch on mount for cached data
      refetchOnMount: (query) => {
        // Only refetch if data is stale
        return query.state.dataUpdatedAt === 0 || 
               Date.now() - query.state.dataUpdatedAt > 120000; // 2 minutes default
      },
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      // Retry delay for mutations
      retryDelay: 1000,
    },
  },
});

// Export cache configurations for use in hooks
export { cacheConfig };

// Enhanced API hooks with optimized caching
export const useApiQuery = <T>(
  key: string[],
  fetcher: () => Promise<T>,
  options?: {
    cacheType?: keyof typeof cacheConfig;
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
    [key: string]: unknown;
  }
) => {
  const { cacheType = 'default', ...restOptions } = options || {};
  const cacheSettings = cacheConfig[cacheType];

  return useQuery({
    queryKey: key,
    queryFn: fetcher,
    staleTime: cacheSettings.staleTime,
    gcTime: cacheSettings.gcTime,
    ...restOptions,
  });
};

/**
 * Utility function to prefetch data for better performance
 */
export const prefetchQuery = async <T>(
  key: string[],
  fetcher: () => Promise<T>,
  cacheType: keyof typeof cacheConfig = 'default'
) => {
  const cacheSettings = cacheConfig[cacheType];
  
  return queryClient.prefetchQuery({
    queryKey: key,
    queryFn: fetcher,
    staleTime: cacheSettings.staleTime,
    gcTime: cacheSettings.gcTime,
  });
};

/**
 * Utility function to invalidate specific cache patterns
 */
export const invalidateQueries = (keyPattern: string[]) => {
  return queryClient.invalidateQueries({ queryKey: keyPattern });
};

/**
 * Utility function to clear cache for memory optimization
 */
export const clearCache = () => {
  queryClient.clear();
};

/**
 * Get cache statistics for monitoring
 */
export const getCacheStats = () => {
  const cache = queryClient.getQueryCache();
  return {
    size: cache.getAll().length,
    queries: cache.getAll().map(query => ({
      key: query.queryKey,
      state: query.state.status,
      dataUpdatedAt: query.state.dataUpdatedAt,
      staleTime: 120000, // Default 2 minutes
    })),
  };
};